import React, { useEffect, useState } from 'react';
import { InputAdornment, IconButton, Box } from '@material-ui/core';
import { CopyComponent, ModalComponent, PasslyTextField } from '@app/components';
import { useTypedTranslation } from '@app/hooks';
import { vaultPasswordsService, vaultPasswordHistoryService } from '@app/services';
import { useDebounce, genericErrorToast, passlyFormatDate, DATE_TIME_FORMAT, getActiveVaultPermission } from '@app/helpers';
import { Warning, History } from '@material-ui/icons';
import { FormMode } from '@app/helpers/enums';
import { vaultAdmin, vaultRead } from '@app/store/constants';
import { ShowVaultPasswordHistoryDialog } from '@app/pages/password/dialogs/showVaultPasswordHistory.dialog';
import { useSelector } from 'react-redux';
import Icon from '@app/components/Icon';
import { PasslyTooltip } from '@app/componentsV2';

interface IProps {
    vaultId: string;
    passwordId?: string;
    updateable?: boolean;
    hovered?: boolean;
    passwordWanted?: string;
    showGenerateButton?: boolean;
    returnGeneratedPassword?: Function;
    formMode?: FormMode;
    showPasswordStrength?: boolean;
    defaultStyle?: boolean;
    showPlaceholder?: boolean;
    label?: string;
    password?: string;
    passwordComplexity?: string;
    disabled?: boolean;
    errorText?: string;
    showExpiration?: boolean;
    showExpiration2?: boolean;
    autoFocus?: boolean;
    revealPassword?: boolean;
    showHistoryIcon?: boolean;
    historyPasswordType?: number;
    isHistoryPassword?: boolean;
    required?: boolean;
}

const VaultPasswordComponent: React.SFC<IProps> = ({
    vaultId,
    passwordId = '',
    passwordWanted = '',
    updateable = false,
    hovered,
    label = '',
    password = '',
    passwordComplexity = '',
    disabled = false,
    showGenerateButton = false,
    returnGeneratedPassword,
    formMode = FormMode.Read,
    showPasswordStrength = false,
    defaultStyle = false,
    showPlaceholder = true,
    errorText = '',
    showExpiration = false,
    showExpiration2 = false,
    autoFocus = false,
    revealPassword = false,
    showHistoryIcon = false,
    historyPasswordType = 0,
    isHistoryPassword = false,
    required
}) => {
    const { translation } = useTypedTranslation();
    const placeholder = 'myplaceholder';

    const [isMasked, setIsMasked] = useState(true);
    const [fetchedPassword, setFetchedPassword] = useState(placeholder);
    const [passwordStrength, setPasswordStrength] = useState('');
    const [passwordStrengthColor, setPasswordStrengthColor] = useState('');
    const [passwordStrengthScore, setPasswordStrengthScore] = useState(0);
    const [isReadonly] = useState(formMode === FormMode.Read);
    const [passwordStrengthBarStyle, setPasswordStrengthBarStyle] = useState({});
    const [expiredMessage, setExpiredMessage] = useState<string>('');

    const debouncedValue = useDebounce(isMasked, 7500);

    const [openHistory, setOpenHistory] = useState<boolean>(false);

    const userVaultsPermissions = useSelector((state: any) => state.signIn.currentUser.vaultPermissions);
    const permissions = userVaultsPermissions ? getActiveVaultPermission(userVaultsPermissions[vaultId]) : new Set<string>();

    const hasHistoryPermission: boolean = permissions?.has(vaultAdmin);
    const isShowRevealPassword: boolean = permissions?.has(vaultRead);

    useEffect(() => {
        if (formMode === FormMode.Edit) {
            setFetchedPassword(password);
            checkPassStrength(password);
            if (showExpiration || showExpiration2) {
                vaultPasswordsService.getExpiredPasswordRecord(vaultId, passwordId, handleGetExpiredRecord);
            }
        } else if (!showPlaceholder) {
            setFetchedPassword('');
            checkPassStrength('');
        } else if (showPasswordStrength) {
            checkPassStrength(fetchedPassword);
        }

        if (revealPassword) {
            onRevealClick();
        }
    }, []);

    useEffect(() => {
        if (!isMasked) {
            setIsMasked(!isMasked);
        }
    }, [debouncedValue]);

    const onRevealClick = () => {
        if (fetchedPassword === placeholder) {
            if (isHistoryPassword) {
                let jsonObject: any = {};
                jsonObject.VaultPasswordHistoryId = passwordId;

                vaultPasswordHistoryService.getVaultPasswordHistoryPassword(jsonObject, vaultId).then((response) => {
                    setFetchedPassword(response.data.Password);
                    setIsMasked(false);
                });
            } else {
                vaultPasswordsService.getProtectedPassword(vaultId, passwordId, passwordWanted).then((response) => {
                    setFetchedPassword(response.data.Password);
                    setIsMasked(false);
                });
            }
        } else {
            setIsMasked(!isMasked);
        }
    };

    const fetchBeforeCopy = () => {
        if (fetchedPassword === placeholder) {
            if (isHistoryPassword) {
                let jsonObject: any = {};
                jsonObject.VaultPasswordHistoryId = passwordId;

                return vaultPasswordHistoryService.getVaultPasswordHistoryPassword(jsonObject, vaultId).then((response) => {
                    setFetchedPassword(response.data.Password);
                    return Promise.resolve(response.data.Password);
                });
            } else {
                return vaultPasswordsService.getProtectedPassword(vaultId, passwordId, passwordWanted).then((response) => {
                    setFetchedPassword(response.data.Password);
                    return Promise.resolve(response.data.Password);
                });
            }
        }

        return Promise.resolve(fetchedPassword);
    };

    const onGenerateClick = () => {
        const complexity = passwordComplexity !== 'default' ? passwordComplexity : '';
        vaultPasswordsService.getGeneratedPassword(complexity, vaultId, handleSuccessGeneration, genericErrorToast);
    };

    const onPasswordChange = (e: any) => {
        const { name, value } = e.target;

        switch (name) {
            case 'actualPassword':
                checkPassStrength(value);
                setFetchedPassword(value);

                if (returnGeneratedPassword) {
                    returnGeneratedPassword(value);
                }
                break;
            default:
                break;
        }
    };

    const handleSuccessGeneration = (response: any) => {
        const value: any = response.data.value.join('');

        checkPassStrength(value);
        setFetchedPassword(value);

        if (returnGeneratedPassword) {
            returnGeneratedPassword(value);
        }
    };

    const checkPassStrength = (pass: string) => {
        const maxScore = 130; // this presumably should be the max score value
        let passwordStrength: string = '';
        let passwordStrengthColor: string = '';
        let passwordStrengthScore: number = 0;

        let score = scorePassword(pass);
        passwordStrengthScore = score;

        if (score > 90) {
            passwordStrength = translation('VERY_STRONG');
            passwordStrengthColor = 'darkgreen';
        } else if (score > 80) {
            passwordStrength = translation('STRONG');
            passwordStrengthColor = 'green';
        } else if (score > 60) {
            passwordStrength = translation('GOOD');
            passwordStrengthColor = 'orange';
        } else if (score > 0 && score <= 60) {
            passwordStrength = translation('WEAK');
            passwordStrengthColor = 'red';
        } else {
            passwordStrength = '';
        }

        // set to max if exceeded.
        passwordStrengthScore = passwordStrengthScore > maxScore ? maxScore : passwordStrengthScore;

        setPasswordStrength(passwordStrength);
        setPasswordStrengthColor(passwordStrengthColor);
        setPasswordStrengthScore(passwordStrengthScore);

        let hrStyle: any = {
            borderColor: passwordStrengthColor,
            width: `${((passwordStrengthScore / maxScore) * 100).toString()}%`
        };

        setPasswordStrengthBarStyle(hrStyle);
    };

    const scorePassword = (pass: string) => {
        let score: number = 0;
        if (!pass) return score;

        if (pass.length < 6) return 5;

        // award every unique letter until 5 repetitions
        let letters: any = new Object();
        for (let i = 0; i < pass.length; i++) {
            letters[pass[i]] = (letters[pass[i]] || 0) + 1;
            score += 5.0 / letters[pass[i]];
        }

        // bonus points for mixing it up
        let variations: any = {
            digits: /\d/.test(pass),
            lower: /[a-z]/.test(pass),
            upper: /[A-Z]/.test(pass),
            nonWords: /\W/.test(pass)
        };

        let variationCount: number = 0;
        for (let check in variations) {
            variationCount += variations[check] === true ? 1 : 0;
        }
        score += (variationCount - 1) * 10;

        return score;
    };

    const getExpiredTranslation = (expiration: string): string => {
        if (Date.now() < Date.parse(expiration)) {
            return translation('PASSWORD_EXPIRES_AT') + ' ' + passlyFormatDate(expiration, DATE_TIME_FORMAT);
        } else {
            return translation('PASSWORD_EXPIRED_AT') + ' ' + passlyFormatDate(expiration, DATE_TIME_FORMAT);
        }
    };

    const handleGetExpiredRecord = (response: any) => {
        let expiration: any = response.data;

        if (expiration.ExpireAt2 !== null && showExpiration2) {
            setExpiredMessage(getExpiredTranslation(expiration.ExpireAt2 as string));
        } else if (expiration.ExpireAt !== null && !expiration.ExpireAt.startsWith('9999') && showExpiration) {
            setExpiredMessage(getExpiredTranslation(expiration.ExpireAt as string));
        }
    };

    const handleModalCloseCallback = () => {
        setOpenHistory(!openHistory);
    };

    const onHistoryClick = () => {
        setOpenHistory(true);
    };

    return (
        <>
            <ModalComponent
                headerTitle={translation('PASSWORD_HISTORY')}
                modalSwitcherCallback={handleModalCloseCallback}
                form={<ShowVaultPasswordHistoryDialog vaultPasswordId={passwordId} vaultPasswordType={historyPasswordType} vaultId={vaultId} />}
                localModalSwitcher={openHistory}
            />

            <div className='vault__password--password'>
                <div className='vault__password--password__wrapper'>
                    <div className='vault__password--password__container'>
                        <div>
                            <PasslyTextField
                                required
                                className='vault__password--password__input'
                                id='addUserPassword-input'
                                label={label}
                                type={isMasked ? 'password' : 'text'}
                                value={fetchedPassword}
                                name='actualPassword'
                                onFocus={onPasswordChange}
                                onChange={onPasswordChange}
                                disabled={disabled}
                                error={errorText !== ''}
                                helperText={errorText}
                                autoFocus={autoFocus}
                                InputProps={{
                                    readOnly: isReadonly,
                                    disableUnderline: true,
                                    endAdornment: (
                                        <InputAdornment position='end' className='vault__password--password__input-adornment'>
                                            {isMasked && isShowRevealPassword ? (
                                                <PasslyTooltip title={translation('REVEAL_PASSWORD_TOOLTIP')}>
                                                    <i className='fa-regular fa-eye' onClick={onRevealClick} />
                                                </PasslyTooltip>
                                            ) : isShowRevealPassword ? (
                                                <PasslyTooltip title={translation('REVEAL_PASSWORD_TOOLTIP')}>
                                                    <i className='fa-regular fa-eye-slash' onClick={onRevealClick} />
                                                </PasslyTooltip>
                                            ) : null}
                                        </InputAdornment>
                                    )
                                }}
                            />
                            {showPasswordStrength && (
                                <div className='vault__password--password__strength'>
                                    <hr className='vault__password--password__strength__indicator' style={passwordStrengthBarStyle} />
                                    <span className='vault__password--password__strength__label' style={{ color: passwordStrengthColor }}>
                                        {passwordStrength}
                                    </span>
                                </div>
                            )}
                        </div>
                        <Box display='flex' className='vault__password--password__controls'>
                            {showGenerateButton && !disabled && (
                                <div>
                                    <PasslyTooltip title={translation('GENERATE_PASSWORD')}>
                                        <IconButton size='small' onClick={onGenerateClick}>
                                            <Icon className='vault__generate-icon' icon='refreshPasswordIco' />
                                        </IconButton>
                                    </PasslyTooltip>
                                </div>
                            )}
                            {isShowRevealPassword && <CopyComponent fetchBeforeCopy={fetchedPassword === placeholder ? fetchBeforeCopy : undefined} valueToCopy={fetchedPassword} hovered={hovered} />}
                            {updateable && <Icon className='vault__generate-icon vault__generate-icon-updateable' icon='refreshPasswordIco' />}
                            {(showExpiration || showExpiration2) && expiredMessage !== '' && (
                                <Box p={0.3}>
                                    <PasslyTooltip title={expiredMessage}>
                                        <Warning color='error' />
                                    </PasslyTooltip>
                                </Box>
                            )}
                            {showHistoryIcon && hasHistoryPermission && (
                                <div>
                                    <PasslyTooltip title={translation('VIEW_PWS_HISTORY')}>
                                        <IconButton size='small' onClick={() => onHistoryClick()}>
                                            <History className='vault__history-icon' fontSize='small' />
                                        </IconButton>
                                    </PasslyTooltip>
                                </div>
                            )}
                        </Box>
                    </div>
                </div>
            </div>
        </>
    );
};

export default VaultPasswordComponent;
